import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const publicHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': process.env.REACT_APP_SERVER_API_KEY,
});

const authHeaders = (accessToken) => ({
  ...publicHeaders(),
  'Authorization': `Bearer ${accessToken}`,
});

// Public KB routes accept an optional bearer token: signed-in users get their
// role-based visibility on top of the public set, anonymous visitors don't.
const optionalAuthHeaders = (getState) => {
  const accessToken = getState().user?.tokens?.accessToken;
  return accessToken ? authHeaders(accessToken) : publicHeaders();
};

// API errors arrive in three shapes: { error, message } from the controllers,
// a bare string or { error } from the auth middleware, or no response at all
// (network). Normalise so components can always read `.message`.
const toErrorPayload = (error) => {
  const { status, data } = error.response || {};
  if (data && typeof data === 'object') {
    return { ...data, status, message: data.message || data.error || `Request failed (${status})` };
  }
  const sessionExpired =
    status === 401 || (status === 500 && typeof data === 'string' && /user information/i.test(data));
  const message = sessionExpired
    ? 'Your session has expired. Please sign in again.'
    : (typeof data === 'string' && data) || error.message;
  return { status, message };
};

// ── Public Thunks ───────────────────────────────────────────────────────

export const fetchCategories = createAsyncThunk(
  'knowledgeBase/fetchCategories',
  async ({ includeInactive = false } = {}, { getState, rejectWithValue }) => {
    // The admin tab asks for hidden categories. Without a token the API would
    // refuse (401), and silently sending the public request instead would
    // make hidden rows vanish from the admin table, so fail loudly up front.
    if (includeInactive && !getState().user?.tokens?.accessToken) {
      return rejectWithValue({ message: 'You must be signed in to manage categories.' });
    }
    try {
      const query = includeInactive ? '?includeInactive=true' : '';
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/kb/categories${query}`, {
        headers: optionalAuthHeaders(getState),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

export const fetchArticles = createAsyncThunk(
  'knowledgeBase/fetchArticles',
  async ({ categoryId, search, page = 1, limit = 20 } = {}, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const params = new URLSearchParams();
      if (categoryId) params.append('categoryId', categoryId);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', limit);

      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/kb/articles?${params.toString()}`, {
        headers: authHeaders(accessToken),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchArticleById = createAsyncThunk(
  'knowledgeBase/fetchArticleById',
  async (articleId, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/kb/articles/${articleId}`, {
        headers: authHeaders(accessToken),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchPopularArticles = createAsyncThunk(
  'knowledgeBase/fetchPopularArticles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/kb/articles/popular`, {
        headers: publicHeaders(),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const askAI = createAsyncThunk(
  'knowledgeBase/askAI',
  async ({ question, conversationHistory, conversationId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/kb/ai/ask`,
        { question, conversationHistory, conversationId },
        { headers: optionalAuthHeaders(getState) }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ── Admin Thunks ────────────────────────────────────────────────────────

export const createArticle = createAsyncThunk(
  'knowledgeBase/createArticle',
  async (articleData, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/articles`,
        articleData,
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateArticle = createAsyncThunk(
  'knowledgeBase/updateArticle',
  async ({ articleId, data }, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/articles/${articleId}`,
        data,
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteArticle = createAsyncThunk(
  'knowledgeBase/deleteArticle',
  async (articleId, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/articles/${articleId}`,
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const autoDraftArticle = createAsyncThunk(
  'knowledgeBase/autoDraftArticle',
  async ({ topic, category }, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/articles/auto-draft`,
        { topic, category },
        { headers: authHeaders(accessToken) }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createCategory = createAsyncThunk(
  'knowledgeBase/createCategory',
  async (categoryData, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/categories`,
        categoryData,
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

export const updateCategory = createAsyncThunk(
  'knowledgeBase/updateCategory',
  async ({ categoryId, data }, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/categories/${categoryId}`,
        data,
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'knowledgeBase/deleteCategory',
  async (categoryId, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/categories/${categoryId}`,
        { headers: authHeaders(accessToken) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error));
    }
  }
);

export const fetchQuestionInsights = createAsyncThunk(
  'knowledgeBase/fetchQuestionInsights',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/kb/admin/questions`,
        { headers: authHeaders(accessToken) }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchConversations = createAsyncThunk(
  'knowledgeBase/fetchConversations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/kb/admin/conversations`, {
        headers: authHeaders(accessToken),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchConversationTurns = createAsyncThunk(
  'knowledgeBase/fetchConversationTurns',
  async (conversationId, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().user.tokens;
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/kb/admin/conversations/${conversationId}`, {
        headers: authHeaders(accessToken),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────

const knowledgeBaseSlice = createSlice({
  name: 'knowledgeBase',
  initialState: {
    categories: [],       // public list (Browse tab)
    adminCategories: [],  // full list incl. hidden (Admin tab); kept separate so a
                          // late public response can never overwrite it
    categoriesError: null,
    articles: [],
    currentArticle: null,
    popularArticles: [],
    aiConversation: [],
    aiConversationId: null,
    aiError: null,
    adminQuestions: [],
    loading: false,
    aiLoading: false,
    error: null,
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    adminConversations: [],
    conversationTurns: null,
    conversationTurnsLoading: false,
  },
  reducers: {
    clearCurrentArticle(state) {
      state.currentArticle = null;
    },
    clearAIConversation(state) {
      state.aiConversation = [];
      state.aiConversationId = null;
      state.aiError = null;
    },
    addUserMessage(state, action) {
      state.aiConversation.push({ role: 'user', content: action.payload });
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCategories
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesError = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        if (action.meta.arg?.includeInactive) {
          state.adminCategories = action.payload;
        } else {
          state.categories = action.payload;
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesError =
          action.payload?.message || action.error?.message || 'Could not load categories.';
      })

      // fetchArticles
      .addCase(fetchArticles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.loading = false;
        state.articles = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchArticleById
      .addCase(fetchArticleById.fulfilled, (state, action) => {
        state.currentArticle = action.payload;
      })

      // fetchPopularArticles
      .addCase(fetchPopularArticles.fulfilled, (state, action) => {
        state.popularArticles = action.payload;
      })

      // askAI
      .addCase(askAI.pending, (state) => {
        state.aiLoading = true;
      })
      .addCase(askAI.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiConversationId = action.payload.conversationId;
        state.aiConversation.push({ role: 'assistant', content: action.payload.answer });
      })
      .addCase(askAI.rejected, (state, action) => {
        state.aiLoading = false;
        state.aiError = action.payload;
        state.aiConversation.push({ role: 'assistant', content: 'Error connecting. Please try again.' });
      })

      // Admin: fetchQuestionInsights
      .addCase(fetchQuestionInsights.fulfilled, (state, action) => {
        state.adminQuestions = action.payload;
      })

      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.adminConversations = action.payload;
      })
      
      .addCase(fetchConversationTurns.pending, (state) => {
        state.conversationTurnsLoading = true;
        state.conversationTurns = null;
      })
      .addCase(fetchConversationTurns.fulfilled, (state, action) => {
        state.conversationTurns = action.payload;
        state.conversationTurnsLoading = false;
      })
      .addCase(fetchConversationTurns.rejected, (state) => {
        state.conversationTurnsLoading = false;
        state.conversationTurns = null;
      });
  },
});

export const { clearCurrentArticle, clearAIConversation, addUserMessage } = knowledgeBaseSlice.actions;
export default knowledgeBaseSlice.reducer;
